import { useState, useRef, useLayoutEffect } from 'react';
import { ResponsiveContainer } from 'recharts';

/**
 * ChartContainer - A wrapper for ResponsiveContainer that fixes CSS Grid dimension issues.
 * 
 * ResponsiveContainer uses getBoundingClientRect() which returns 0 for elements 
 * that haven't been painted yet in a grid container. This wrapper measures
 * the actual container width after layout and passes it to the chart.
 */
export const ChartContainer = ({ children, height = 280, minHeight }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: height });

  useLayoutEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        if (width > 0) {
          setDimensions({ width: Math.floor(width), height });
        }
      }
    };

    // Measure after initial layout
    measureContainer();

    // Also measure on resize
    const resizeObserver = new ResizeObserver(() => {
      measureContainer();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: minHeight || height, 
        minHeight: minHeight || height,
        position: 'relative'
      }}
    >
      {dimensions.width > 0 && (
        <ResponsiveContainer width={dimensions.width} height={height}>
          {children}
        </ResponsiveContainer>
      )}
    </div>
  );
};
